import { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { Link, useNavigate } from "react-router-dom";
import { AdminRoutes } from "config/routes";
import {
  useGetNotificationsForUserQuery,
  useMarkAllNotificationsReadMutation,
  useUpdateNotificationViewedMutation,
} from "graphql/__generated__/hooks";
import { useAuthUser } from "hooks/useAuthUser";

const PREVIEW_LIMIT = 8;

type HeaderNotificationMenuProps = {
  unreadCount: number;
  refetchUnreadCount: () => Promise<any>;
};

export const HeaderNotificationMenu = ({
  unreadCount,
  refetchUnreadCount,
}: HeaderNotificationMenuProps) => {
  const { user } = useAuthUser();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { data, loading, refetch } = useGetNotificationsForUserQuery({
    variables: { userId: user?.user_id || "" },
    skip: !user?.user_id,
  });

  useEffect(() => {
    if (open && user?.user_id) {
      void refetch();
      void refetchUnreadCount();
    }
  }, [open, user?.user_id, refetch, refetchUnreadCount]);

  const [markAllRead, { loading: markingAll }] =
    useMarkAllNotificationsReadMutation();
  const [updateViewed] = useUpdateNotificationViewedMutation();

  const items = data?.notification ?? [];
  const preview = items.slice(0, PREVIEW_LIMIT);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAllRead = async () => {
    if (!user?.user_id) return;
    await markAllRead({ variables: { userId: user.user_id } });
    await refetch();
    await refetchUnreadCount();
  };

  const openNotificationLink = (link: string) => {
    if (link.startsWith("http://") || link.startsWith("https://")) {
      window.open(link, "_blank", "noopener,noreferrer");
      return;
    }
    const path = link.startsWith("/") ? link : `/${link}`;
    navigate(path);
  };

  const handleNotificationClick = async (n: {
    notification_id: string;
    text: string;
    link: string;
    already_viewed: boolean;
  }) => {
    if (!n.already_viewed) {
      await updateViewed({
        variables: {
          notificationId: n.notification_id,
          alreadyViewed: true,
        },
      });
      await refetch();
      await refetchUnreadCount();
    }
    if (n.link) {
      openNotificationLink(n.link);
    }
    handleClose();
  };

  if (!user?.user_id) {
    return null;
  }

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          onClick={handleOpen}
          sx={{ color: "white" }}
          size="large"
          aria-label="Notifications"
          aria-expanded={open ? "true" : undefined}
          aria-haspopup="true"
        >
          <Badge
            color="error"
            badgeContent={unreadCount > 0 ? unreadCount : undefined}
            max={99}
            invisible={unreadCount === 0}
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        id="header-notifications-menu"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              width: { xs: "min(100vw - 32px, 360px)", sm: 360 },
              maxHeight: 440,
              mt: 1,
            },
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              onClick={handleMarkAllRead}
              disabled={markingAll}
            >
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : preview.length === 0 ? (
          <Typography color="text.secondary" sx={{ px: 2, py: 2 }}>
            No notifications yet.
          </Typography>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 280, overflow: "auto" }}>
            {preview.map((n) => (
              <ListItemButton
                key={n.notification_id}
                alignItems="flex-start"
                onClick={() => handleNotificationClick(n)}
                sx={{ py: 1.25 }}
              >
                <ListItemText
                  primary={n.text}
                  secondary={n.link ? n.link : undefined}
                  primaryTypographyProps={{
                    variant: "body2",
                    fontWeight: n.already_viewed ? 400 : 600,
                  }}
                  secondaryTypographyProps={{
                    variant: "caption",
                    sx: {
                      wordBreak: "break-all",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    },
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        )}

        <Divider />
        <Box sx={{ py: 0.5 }}>
          <Button
            fullWidth
            component={Link}
            to={AdminRoutes.Notifications}
            onClick={handleClose}
            sx={{ justifyContent: "center", py: 1 }}
          >
            View all notifications
          </Button>
        </Box>
      </Popover>
    </>
  );
};
